'use client';

import { useEffect, useRef, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { SkeletonBlock } from '@/src/components/ui/SkeletonBlock';
import { onSlotRenderEnded } from '@/src/lib/ads/gpt';
import { getVisualViewportBottom } from '@/src/lib/utils/viewport';

// GPT's 805px creative container would otherwise widen the cell's 1fr column.
const adSlotItem = css({
  display: 'flex',
  minWidth: 0,
});

// The skeleton overlays the reserved surface, so the item anchors it.
const adSlotItemBase = css({
  position: 'relative',
});

// The height reservation lives on the wrapper, because GPT's
// `collapseDiv: 'ON_NO_FILL'` hides its own inner div on an empty response —
// a reservation on the surface would vanish with it and shift layout anyway.
// Applied conditionally rather than overridden when collapsed: Panda's atomic
// order in utilities.css decides between two min-height classes, not cx order.
const adSlotItemReserved = css({
  // Legacy reserved 360px, but the fluid native creative GAM actually serves
  // renders a 385px iframe (392.5px item on phone, 405px on desktop), so every
  // in-viewport fill shifted the cards below it — a measured real-user CLS
  // source on the mobile one-column grid. Reserve the tallest observed filled
  // height instead; over-reserving costs whitespace, never a shift.
  // eslint-disable-next-line hotpads/no-magic-numbers -- measured SRP fluid ad creative height, see comment
  minHeight: '405px',
});

// An unfilled slot must keep its container in the DOM: the slot stays defined
// and `pubads().refresh` keeps re-requesting it, and GPT can only render into a
// div that still exists. Unmounting it — what this did before — turned every
// later request on that slot into one GAM could never fill. Height, not
// `display`, so the container keeps its width and GPT still measures a `fluid`
// creative correctly on the refresh that fills it.
const adSlotItemUnfilled = css({
  height: 0,
  overflow: 'hidden',
});

const adSlotSurface = css({
  minWidth: 0,
  width: '100%',
  // The slot requests a `fluid` size, which GPT measures from the creative's own
  // content. Stretching the iframe into a fixed box instead — what this did
  // before — crops taller creatives and squashes the 805x115 banner.
  '& iframe': {
    // The creative owns its bottom border, so only clip the host-owned top corners.
    clipPath: 'inset(0 round token(radii.md) token(radii.md) 0 0)',
    maxWidth: '100%',
  },
});

const adSlotSkeleton = css({
  position: 'absolute',
  inset: 0,
});

type RenderState = 'pending' | 'filled' | 'unfilled';

type Props = {
  id: string;
  /** Style hook for the feed's snap slot; the grid uses the default cell. */
  className?: string;
};

export function SrpAdSlot({ id, className }: Props) {
  const [renderState, setRenderState] = useState<RenderState>('pending');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);

  // GPT's `collapseEmptyDivs` only hides its own div, which would leave this
  // grid cell as a hole between listing cards. Every render end reports in, so a
  // later refresh that fills the slot expands it again.
  useEffect(
    () =>
      onSlotRenderEnded(id, (isEmpty) => {
        if (!isEmpty) {
          setRenderState('filled');
          setIsCollapsed(false);
          return;
        }
        setRenderState('unfilled');
        // Collapsing an on-screen box yanks everything below it up by the
        // reserved height — a layout shift the user watches happen. Only
        // reclaim the space when the slot is still below the visible viewport,
        // and once collapsed stay collapsed: expanding on a later no-fill
        // would shift layout again.
        setIsCollapsed((wasCollapsed) => {
          if (wasCollapsed) return true;
          const rect = itemRef.current?.getBoundingClientRect();
          return rect !== undefined && rect.top >= getVisualViewportBottom();
        });
      }),
    [id],
  );

  return (
    <li
      ref={itemRef}
      data-srp-ad-unfilled={renderState === 'unfilled' ? 'true' : 'false'}
      data-srp-ad-collapsed={isCollapsed ? 'true' : 'false'}
      className={cx(adSlotItemBase, className ?? adSlotItem, isCollapsed ? adSlotItemUnfilled : adSlotItemReserved)}
    >
      <div id={id} className={adSlotSurface} />
      {renderState === 'pending' && <SkeletonBlock data-srp-ad-skeleton radius="md" className={adSlotSkeleton} />}
    </li>
  );
}
